import React, { useState, useEffect } from 'react';
import { Clock, Filter, Search, Activity } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import Layout from '../components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActivityTimeline = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchActivities();
  }, [filter]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== 'all') {
        params.entity_type = filter;
      }
      
      const response = await axios.get(`${API}/activity/timeline`, {
        headers: getAuthHeaders(),
        params
      });
      
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'created': return '➕';
      case 'updated': return '✏️';
      case 'completed': return '✅';
      case 'approved': return '👍';
      case 'rejected': return '👎';
      case 'deleted': return '🗑️';
      case 'analyzed': return '🔍';
      case 'generated': return '⚡';
      default: return '📝';
    }
  };

  const getEntityColor = (entityType) => {
    switch (entityType) {
      case 'invention': return 'text-purple-400';
      case 'supplier': return 'text-blue-400';
      case 'quote': return 'text-green-400';
      case 'milestone': return 'text-yellow-400';
      case 'document': return 'text-orange-400';
      case 'analysis': return 'text-pink-400';
      default: return 'text-gray-400';
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredActivities = activities.filter(activity =>
    activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.entity_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Clock className="w-8 h-8 text-purple-500" />
            Activity Timeline
          </h1>
          <p className="text-gray-400 mt-1">Complete history of everything LEE has done</p>
        </div>

        {/* Search & Filters */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Types</option>
            <option value="invention">Inventions</option>
            <option value="supplier">Suppliers</option>
            <option value="quote">Quotes</option>
            <option value="milestone">Milestones</option>
            <option value="document">Documents</option>
            <option value="analysis">Analysis</option>
          </select>
        </div>

        {/* Timeline */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
            <p className="text-gray-400 mt-4">Loading activities...</p>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 rounded-lg">
            <Activity className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No activities found</p>
            <p className="text-gray-500 text-sm mt-2">Activities will appear as LEE works</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-700"></div>

            {/* Activities */}
            <div className="space-y-6">
              {filteredActivities.map((activity, index) => (
                <div key={activity.id} className="relative pl-16">
                  {/* Timeline Dot */}
                  <div className="absolute left-4 top-2 w-4 h-4 rounded-full bg-purple-500 border-4 border-gray-900"></div>

                  {/* Activity Card */}
                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{getActionIcon(activity.action_type)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold ${getEntityColor(activity.entity_type)}`}>
                            {activity.entity_type.toUpperCase()}
                          </span>
                          <span className="text-gray-400">·</span>
                          <span className="text-gray-400 text-sm">{formatDate(activity.created_at)}</span>
                        </div>
                        <p className="text-white font-medium mb-1">{activity.entity_name}</p>
                        <p className="text-gray-300 text-sm">{activity.description}</p>
                        
                        {/* Metadata */}
                        {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {Object.entries(activity.metadata).map(([key, value]) => (
                              <span
                                key={key}
                                className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded"
                              >
                                {key}: {typeof value === 'object' ? JSON.stringify(value) : value}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ActivityTimeline;
